import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import {
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { Box } from '~/components/elements/Box'
import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { VStack } from '~/components/elements/VStack'
import {
  ensureExists,
  exists,
  findByPath,
  mapItemsToObj,
  unassignedSymbol,
} from '~/utils'

import { dialogInfoAtom, linkCustomerAttributesInfoAtom } from '../../atoms'
import { useCloseDialogWithRefresh } from '../../hooks/useCloseDialogWithRefresh'
import { useUpdateCustomerAttributeGroupsMutation } from '../../hooks/useUpdateCustomerAttributeGroupsMutation'
import {
  LinkCustomerAttributesDialog,
  type LinkCustomerAttributesDialogProps,
} from '../LinkCustomerAttributesDialog'

import { CustomerAttributeGroupInput } from './CustomerAttributeGroupInput'
import { type InputValues, resolver } from './utils'

export const CustomerAttributeGroupSettingsDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const surveyGroup = ensureExists(
    dialogInfo?.surveyGroup,
    t('assertion_failed'),
  )
  const defaultCustomerAttributeGroups = React.useMemo(
    () =>
      surveyGroup.customerAttributeGroups.map((customerAttributeGroup) => ({
        ...R.pick(customerAttributeGroup, ['id', 'name', 'showCustomizedKPI']),
        attachedCustomerAttributeGroupMap: mapItemsToObj(
          surveyGroup.surveys,
          (survey) => {
            const attachedCustomerAttributeGroup = findByPath(
              customerAttributeGroup.attachedCustomerAttributeGroups,
              'surveyId',
              survey.id,
            )

            return exists(attachedCustomerAttributeGroup)
              ? (attachedCustomerAttributeGroup.customerAttributeGroupId ??
                  null)
              : unassignedSymbol
          },
        ),
        customerAttributes: customerAttributeGroup.customerAttributes.map(
          (customerAttribute) => ({
            ...R.pick(customerAttribute, ['id', 'name']),
            attachedCustomerAttributeMap: mapItemsToObj(
              surveyGroup.surveys,
              (survey) => {
                const attachedCustomerAttribute = findByPath(
                  customerAttribute.attachedCustomerAttributes,
                  'surveyId',
                  survey.id,
                )

                return exists(attachedCustomerAttribute)
                  ? (attachedCustomerAttribute.order ?? null)
                  : unassignedSymbol
              },
            ),
          }),
        ),
      })) satisfies InputValues['customerAttributeGroups'],
    [surveyGroup.customerAttributeGroups, surveyGroup.surveys],
  )

  const methods = useForm<InputValues>({
    defaultValues: { customerAttributeGroups: defaultCustomerAttributeGroups },
    resolver,
  })
  const { control, handleSubmit, setValue } = methods
  const { append, fields, remove } = useFieldArray({
    control,
    keyName: 'fieldId',
    name: 'customerAttributeGroups',
  })

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const updateCustomerAttributeGroupsMutation =
    useUpdateCustomerAttributeGroupsMutation({
      onSuccess: closeDialogWithRefresh,
    })
  const resetDialogInfo = useResetAtom(dialogInfoAtom)

  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ customerAttributeGroups }) => {
      if (
        R.isDeepEqual(customerAttributeGroups, defaultCustomerAttributeGroups)
      ) {
        resetDialogInfo()

        return
      }

      updateCustomerAttributeGroupsMutation.mutate({
        customerAttributeGroups,
        defaultCustomerAttributeGroups,
        surveyGroupId: surveyGroup.id,
      })
    },
    [
      defaultCustomerAttributeGroups,
      resetDialogInfo,
      surveyGroup.id,
      updateCustomerAttributeGroupsMutation,
    ],
  )

  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    append({
      name: '',
      attachedCustomerAttributeGroupMap: mapItemsToObj(
        surveyGroup.surveys,
        () => unassignedSymbol,
      ),
      customerAttributes: [],
      showCustomizedKPI: true,
    })
  }, [append, surveyGroup.surveys])

  const linkCustomerAttributesInfo = useAtomValue(
    linkCustomerAttributesInfoAtom,
  )
  const handleLinkCustomerAttributesDialogExecute = React.useCallback<
    EH<LinkCustomerAttributesDialogProps, 'onExecute'>
  >(
    (index, customerAttributes) => {
      setValue(
        `customerAttributeGroups.${index}.customerAttributes`,
        customerAttributes,
      )
    },
    [setValue],
  )

  return (
    <>
      <FormProvider {...methods}>
        <ExecuteCancelDialog
          executing={updateCustomerAttributeGroupsMutation.isPending}
          mode="settings"
          title={t('surveygroup_title_customized_segmentsettings', {
            surveyGroup,
          })}
          width="100%"
          onCancel={resetDialogInfo}
          onExecute={handleSubmit(handleValid)}
        >
          <VStack spacing={0}>
            <Box padding={2}>
              <StyledButton icon="add" onClick={handleAddButtonClick}>
                {t('add_customized_segments_group')}
              </StyledButton>
            </Box>

            {fields.map((field, index) => (
              <CustomerAttributeGroupInput
                key={field.fieldId}
                index={index}
                onDelete={remove}
              />
            ))}
          </VStack>
        </ExecuteCancelDialog>
      </FormProvider>

      {exists(linkCustomerAttributesInfo) && (
        <LinkCustomerAttributesDialog
          onExecute={handleLinkCustomerAttributesDialogExecute}
        />
      )}
    </>
  )
}
